/**
 * Textarea Component
 *
 * Glass-inset styled multi-line text input with theme-aware colors.
 */

import React from 'react';

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  hint?: string;
}

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ label, error, hint, className = '', id, ...props }, ref) => {
    const textareaId = id || `textarea-${Math.random().toString(36).slice(2, 11)}`;

    const baseClasses = `
      w-full px-4 py-3
      border rounded-xl
      bg-[rgba(0,0,0,0.03)]
      text-[var(--text-primary)] placeholder-[var(--text-tertiary)]
      transition-all duration-150
      focus:ring-2 focus:border-transparent focus:outline-none
      disabled:opacity-50 disabled:cursor-not-allowed
      resize-none
    `;

    const stateClasses = error
      ? 'border-[#FF3B30]/50 focus:ring-[#FF3B30]/20'
      : 'border-[var(--border-default)] focus:ring-[#1A1A1A]/20 focus:border-[#1A1A1A]/40';

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={textareaId}
            className="block text-sm font-medium text-[var(--text-secondary)] mb-2"
          >
            {label}
          </label>
        )}
        <textarea
          ref={ref}
          id={textareaId}
          className={`${baseClasses} ${stateClasses} ${className}`.trim().replace(/\s+/g, ' ')}
          {...props}
        />
        {error && (
          <p className="mt-1.5 text-sm text-[#FF3B30]">{error}</p>
        )}
        {hint && !error && (
          <p className="mt-1.5 text-sm text-[var(--text-tertiary)]">{hint}</p>
        )}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';
